import { useState, useEffect, useCallback, useRef } from 'react';
import DashboardApiService from 'Services/ApiServices/DashboardApiService';
import F2WCustomLogger from 'Services/F2WCustomLogger';

const AUTO_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

const useDashboardAnalytics = (season, dateFrom, dateTo) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const previousDataRef = useRef(null);

  const fetchData = useCallback(
    async (isRefresh = false) => {
      try {
        if (isRefresh) {
          setIsRefreshing(true);
        } else {
          setIsLoading(true);
        }
        setError(null);

        const result = await DashboardApiService.getAnalytics(season, dateFrom, dateTo);
        const analyticsData = result?.data || result;
        previousDataRef.current = analyticsData;
        setData(analyticsData);
      } catch (err) {
        if (isRefresh && previousDataRef.current) {
          setData(previousDataRef.current);
        }
        setError(err);
        F2WCustomLogger.error('Failed to fetch dashboard analytics', err, {
          component: 'useDashboardAnalytics',
          action: 'fetchData',
          details: { season, dateFrom, dateTo, isRefresh },
        });
      } finally {
        if (isRefresh) {
          setIsRefreshing(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [season, dateFrom, dateTo]
  );

  // Initial fetch and refetch when params change
  // Skip fetch when season is empty — Dashboard sets it after first load
  useEffect(() => {
    if (!season) return;
    fetchData(false);
  }, [fetchData, season]);

  // Auto-refresh every 5 minutes — only when season is set
  useEffect(() => {
    if (!season) return;
    const interval = setInterval(() => fetchData(true), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData, season]);

  return { data, isLoading, isRefreshing, error, refetch: fetchData };
};

export default useDashboardAnalytics;
